import { Directive, ElementRef, HostListener, OnInit, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Dyrektywa, która czyści wartość inputa po uzyskaniu focusu i przywraca
 * oryginalną wartość po utracie focusu, jeśli użytkownik nic nie wpisał.
 * Idealna dla pól ceny i ilości - użytkownik może od razu zacząć wpisywać nową wartość.
 *
 * Działa z: [value]+(change), [(ngModel)], [ngModel]+(ngModelChange)
 *
 * Użycie:
 * <input clearOnFocus ... />
 * <input matInput clearOnFocus ... />
 */
@Directive({
  selector: '[clearOnFocus]',
  standalone: true
})
export class ClearOnFocusDirective implements OnInit {
  private _inputElement: HTMLInputElement | null = null;
  private _originalValue: string | number | null = null;
  private _wasCleared = false;

  constructor(
    private _elementRef: ElementRef,
    @Optional() private _ngControl: NgControl
  ) {}

  ngOnInit(): void {
    this._findInputElement();
  }

  private _findInputElement(): void {
    const el = this._elementRef.nativeElement;

    if (el.tagName === 'INPUT') {
      this._inputElement = el;
      return;
    }

    this._inputElement = el.querySelector('input');
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this._inputElement) {
      this._findInputElement();
    }

    // Zapisz oryginalną wartość przed wyczyszczeniem
    if (this._ngControl?.control) {
      this._originalValue = this._ngControl.control.value;
    } else if (this._inputElement) {
      this._originalValue = this._inputElement.value;
    }

    this._wasCleared = true;

    // Wyczyść input po cyklu change detection Angulara
    // (setTimeout zapobiega nadpisaniu przez [ngModel] / [value] binding)
    setTimeout(() => {
      if (this._inputElement && this._wasCleared) {
        this._inputElement.value = '';
      }
    });
  }

  @HostListener('blur')
  onBlur(): void {
    if (!this._wasCleared) return;

    if (!this._inputElement) {
      this._findInputElement();
    }

    // Sprawdź czy użytkownik coś wpisał
    const currentValue = this._inputElement?.value;

    // Jeśli wartość jest pusta - użytkownik nic nie wpisał, przywróć oryginał
    if (!currentValue) {
      if (this._inputElement) {
        this._inputElement.value = String(this._originalValue ?? '');
      }

      // Dla NgControl (ngModel) - zsynchronizuj wartość z powrotem do form control
      if (this._ngControl?.control) {
        this._ngControl.control.setValue(this._originalValue, { emitEvent: false });
      }
    }

    this._wasCleared = false;
    this._originalValue = null;
  }
}
